import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Folder, FileArchive, Download, Lock, Clock as Unlock, Plus, CreditCard as Edit, Trash2, X, Upload, Calendar, Eye, EyeOff, ChartBar as BarChart3, HardDrive, ChevronDown, ChevronUp, Shield, RefreshCw, CircleAlert as AlertCircle, Youtube, Video, Key } from 'lucide-react';
import { useSupabaseFiles, FileCategory, FileItem } from '../hooks/useSupabaseFiles';
import { useSiteSettings } from '../hooks/useSiteSettings';
import PasswordManagerModal from '../components/PasswordManagerModal';

function ErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center justify-between gap-4 mb-6"
    >
      <div className="flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
        <span className="text-red-300 text-sm">{message}</span>
      </div>
      <button
        onClick={onRetry}
        className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm font-medium transition-all flex-shrink-0"
      >
        <RefreshCw className="w-4 h-4" />
        {t('files.refreshPage')}
      </button>
    </motion.div>
  );
}

function extractYouTubeId(url: string): string | null {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/,
    /youtube\.com\/v\/([\w-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function FilesPage() {
  const { t } = useTranslation();
  const files = useSupabaseFiles();
  const { settings, updateSetting } = useSiteSettings();
  const isAdmin = localStorage.getItem('admin_logged_in') === 'true';

  const videoUrl = settings.files_video_tutorial_url ?? '';
  const videoId = extractYouTubeId(videoUrl);

  const [showVideoTutorial, setShowVideoTutorial] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showPasswordManager, setShowPasswordManager] = useState(false);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [passwordInputs, setPasswordInputs] = useState<Record<string, string>>({});
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});
  const [unlockedFiles, setUnlockedFiles] = useState<Set<string>>(new Set());

  // Admin modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<FileCategory | null>(null);
  const [showItemModal, setShowItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState<FileItem | null>(null);
  const [showStats, setShowStats] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const filteredItems = useMemo(() => {
    if (selectedCategory) return files.items.filter((i) => i.category_id === selectedCategory);
    return files.items;
  }, [files.items, selectedCategory]);

  const handleDownload = async (item: FileItem) => {
    const pw = passwordInputs[item.id] ?? '';
    if (item.has_password && !pw) return;
    setDownloading((p) => ({ ...p, [item.id]: true }));
    setPasswordErrors((p) => ({ ...p, [item.id]: '' }));
    const result = await files.verifyPassword(item.id, pw);
    if (result.error) {
      setPasswordErrors((p) => ({ ...p, [item.id]: result.error! }));
      setDownloading((p) => ({ ...p, [item.id]: false }));
    } else if (result.url) {
      setUnlockedFiles((s) => new Set(s).add(item.id));
      window.open(result.url, '_blank');
      setDownloading((p) => ({ ...p, [item.id]: false }));
      setTimeout(() => files.fetchItems(), 1000);
    } else if (result.blob) {
      setUnlockedFiles((s) => new Set(s).add(item.id));
      const blobUrl = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      // Use the original filename with extension from the verify response
      a.download = result.filename || item.name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      setDownloading((p) => ({ ...p, [item.id]: false }));
      setTimeout(() => files.fetchItems(), 1000);
    }
  };

  const toggleCategoryExpand = (id: string) => {
    setExpandedCategories((s) => {
      const ns = new Set(s);
      if (ns.has(id)) ns.delete(id);
      else ns.add(id);
      return ns;
    });
  };

  const [pageError, setPageError] = useState<string | null>(null);

  const handleRefreshPage = useCallback(() => {
    setPageError(null);
    files.fetchAll();
  }, [files]);

  useEffect(() => {
    if (files.error && !files.loading) {
      setPageError(files.error);
    }
  }, [files.error, files.loading]);

  if (files.loading && !pageError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20 flex items-center justify-center">
        <p className="text-gray-400 text-lg">{t('common.loading')}</p>
      </div>
    );
  }

  if (pageError && files.categories.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20 pb-20">
        <section className="py-12 px-4 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <HardDrive className="w-12 h-12 text-[#EE1C23] mr-3" />
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron">
                  {t('files.title')}
                </h1>
              </div>
            </div>
            <ErrorBanner message={pageError} onRetry={handleRefreshPage} />
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 pt-20 pb-20">
      {/* Header */}
      <section className="py-12 px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-4">
              <HardDrive className="w-12 h-12 text-[#EE1C23] mr-3" />
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#EE1C23] via-red-500 to-red-600 font-orbitron">
                {t('files.title')}
              </h1>
            </div>
            <p className="text-gray-400 text-sm sm:text-base max-w-2xl mx-auto">
              {t('files.subtitle')}
            </p>
          </motion.div>

          {pageError && <ErrorBanner message={pageError} onRetry={handleRefreshPage} />}

          {/* Video tutorial */}
          {videoId && (
            <VideoTutorialSection
              videoId={videoId}
              expanded={showVideoTutorial}
              onToggle={() => setShowVideoTutorial(!showVideoTutorial)}
            />
          )}

          {/* Admin toolbar */}
          {isAdmin && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-wrap gap-3 justify-center mb-8"
            >
              <button
                onClick={() => { setEditingCategory(null); setShowCategoryModal(true); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EE1C23] to-red-600 text-white text-sm font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all"
              >
                <Plus className="w-4 h-4" />
                {t('files.addCategory')}
              </button>
              <button
                onClick={() => { setEditingItem(null); setShowItemModal(true); }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-700 text-white text-sm font-semibold hover:from-red-700 hover:to-red-600 transition-all"
              >
                <Upload className="w-4 h-4" />
                {t('files.addFile')}
              </button>
              <button
                onClick={() => setShowStats(!showStats)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-[#EE1C23]/30 text-white text-sm font-semibold hover:bg-white/20 transition-all"
              >
                <BarChart3 className="w-4 h-4" />
                {t('files.statistics')}
              </button>
              <button
                onClick={() => setShowVideoModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-[#EE1C23]/30 text-white text-sm font-semibold hover:bg-white/20 transition-all"
              >
                <Video className="w-4 h-4" />
                {t('files.setVideoTutorial')}
              </button>
              <button
                onClick={() => setShowPasswordManager(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-[#EE1C23]/30 text-white text-sm font-semibold hover:bg-white/20 transition-all"
              >
                <Key className="w-4 h-4" />
                {t('files.passwords.button')}
              </button>
            </motion.div>
          )}

          {/* Stats panel */}
          {isAdmin && showStats && <StatsPanel files={files} />}

          {/* Category filter */}
          {files.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  selectedCategory === null
                    ? 'bg-[#EE1C23] text-white'
                    : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                }`}
              >
                {t('common.all')}
              </button>
              {files.categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    selectedCategory === cat.id
                      ? 'bg-[#EE1C23] text-white'
                      : 'bg-white/5 text-gray-300 hover:bg-white/10 border border-white/10'
                  }`}
                >
                  <Folder className="w-3.5 h-3.5" />
                  {cat.name}
                  <span className="text-xs opacity-60">({cat.file_count})</span>
                </button>
              ))}
            </div>
          )}

          {/* Categories with files */}
          {files.categories.length === 0 ? (
            <div className="text-center py-20">
              <Folder className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">{t('files.noCategories')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {(selectedCategory
                ? files.categories.filter((c) => c.id === selectedCategory)
                : files.categories
              ).map((category) => {
                const catItems = filteredItems.filter((i) => i.category_id === category.id);
                const isExpanded = expandedCategories.has(category.id) || !!selectedCategory;
                return (
                  <motion.div
                    key={category.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl overflow-hidden"
                  >
                    {/* Category header */}
                    <div
                      className="flex items-center justify-between p-5 cursor-pointer hover:bg-white/5 transition-colors"
                      onClick={() => toggleCategoryExpand(category.id)}
                    >
                      <div className="flex items-center gap-3">
                        <Folder className="w-6 h-6 text-[#EE1C23]" />
                        <div>
                          <h3 className="text-white font-semibold text-lg">{category.name}</h3>
                          {category.description && (
                            <p className="text-gray-400 text-sm">{category.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <div className="text-gray-300 text-sm font-medium">
                            {t('files.totalDownloads')}: {category.total_downloads}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {catItems.length} {t('files.files')}
                          </div>
                        </div>
                        {isAdmin && (
                          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => { setEditingCategory(category); setShowCategoryModal(true); }}
                              className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { if (confirm(t('files.confirmDeleteCategory'))) files.deleteCategory(category.id); }}
                              className="p-1.5 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                      </div>
                    </div>

                    {/* Files list */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 pb-5 space-y-3">
                            {catItems.length === 0 ? (
                              <p className="text-gray-500 text-sm py-4 text-center">{t('files.noFiles')}</p>
                            ) : (
                              catItems.map((item) => (
                                <FileRow
                                  key={item.id}
                                  item={item}
                                  isAdmin={isAdmin}
                                  passwordInput={passwordInputs[item.id] ?? ''}
                                  passwordError={passwordErrors[item.id] ?? ''}
                                  downloading={downloading[item.id] ?? false}
                                  unlocked={unlockedFiles.has(item.id)}
                                  onPasswordChange={(v) => setPasswordInputs((p) => ({ ...p, [item.id]: v }))}
                                  onDownload={() => handleDownload(item)}
                                  onEdit={() => { setEditingItem(item); setShowItemModal(true); }}
                                  onDelete={() => { if (confirm(t('files.confirmDeleteFile'))) files.deleteItem(item.id); }}
                                />
                              ))
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Admin modals */}
      {showCategoryModal && (
        <CategoryModal
          category={editingCategory}
          onClose={() => setShowCategoryModal(false)}
          onSave={async (name, desc, order) => {
            if (editingCategory) {
              await files.updateCategory(editingCategory.id, { name, description: desc, order_index: order });
            } else {
              await files.createCategory(name, desc, order);
            }
            setShowCategoryModal(false);
          }}
        />
      )}

      {showItemModal && (
        <ItemModal
          item={editingItem}
          categories={files.categories}
          onClose={() => setShowItemModal(false)}
          onSave={async (data, onProgress) => {
            if (editingItem) {
              await files.updateItem(editingItem.id, data, onProgress);
            } else {
              await files.createItem(data, onProgress);
            }
            setShowItemModal(false);
          }}
        />
      )}

      {showPasswordManager && (
        <PasswordManagerModal
          api={files}
          categories={files.categories}
          onClose={() => setShowPasswordManager(false)}
        />
      )}

      {showVideoModal && (
        <VideoTutorialModal
          currentUrl={videoUrl}
          onClose={() => setShowVideoModal(false)}
          onSave={async (url) => {
            await updateSetting('files_video_tutorial_url', url);
            setShowVideoModal(false);
          }}
          onRemove={async () => {
            await updateSetting('files_video_tutorial_url', '');
            setShowVideoModal(false);
          }}
        />
      )}
    </div>
  );
}

// ============================================================
// Video Tutorial Section
// ============================================================

function VideoTutorialSection({ videoId, expanded, onToggle }: {
  videoId: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-8"
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-[#EE1C23]/20 to-red-600/10 border border-[#EE1C23]/30 hover:border-[#EE1C23]/50 transition-all group"
      >
        <div className="flex items-center gap-3">
          <div className="bg-[#EE1C23]/30 rounded-lg p-2">
            <Youtube className="w-6 h-6 text-[#EE1C23]" />
          </div>
          <span className="text-white font-semibold text-base sm:text-lg">
            {t('files.videoTutorial')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 rounded-2xl bg-black/60 border border-[#EE1C23]/20 p-4">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${videoId}`}
                  title="Video tutorial"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-xl"
                />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ============================================================
// Video Tutorial Modal (admin)
// ============================================================

function VideoTutorialModal({ currentUrl, onClose, onSave, onRemove }: {
  currentUrl: string;
  onClose: () => void;
  onSave: (url: string) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [url, setUrl] = useState(currentUrl);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(url.trim());
    } catch {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    setSaving(true);
    try {
      await onRemove();
    } catch {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 border border-[#EE1C23]/30 rounded-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">{t('files.setVideoTutorial')}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-sm mb-1 block">{t('files.videoTutorialUrl')}</label>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t('files.videoTutorialPlaceholder')}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:border-[#EE1C23]/50 focus:outline-none"
            />
            {url && !extractYouTubeId(url) && (
              <p className="text-yellow-400/70 text-xs mt-1">YouTube URL not recognized, but will be saved anyway.</p>
            )}
          </div>
        </div>
        <div className="flex gap-3 mt-6">
          {currentUrl && (
            <button
              onClick={handleRemove}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-red-500/20 text-red-300 text-sm font-medium hover:bg-red-500/30 transition-all disabled:opacity-40"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg bg-white/10 text-gray-300 text-sm font-medium hover:bg-white/20 transition-all">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EE1C23] to-red-600 text-white text-sm font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all disabled:opacity-40"
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================================
// File Row
// ============================================================

interface FileRowProps {
  item: FileItem;
  isAdmin: boolean;
  passwordInput: string;
  passwordError: string;
  downloading: boolean;
  unlocked: boolean;
  onPasswordChange: (v: string) => void;
  onDownload: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function FileRow({ item, isAdmin, passwordInput, passwordError, downloading, unlocked, onPasswordChange, onDownload, onEdit, onDelete }: FileRowProps) {
  const { t } = useTranslation();
  const [showPw, setShowPw] = useState(false);

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4 hover:border-[#EE1C23]/30 transition-all">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className="bg-[#EE1C23]/20 rounded-lg p-2 flex-shrink-0">
            <FileArchive className="w-5 h-5 text-[#EE1C23]" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-white font-medium text-sm truncate">{item.name}</h4>
            {item.description && (
              <p className="text-gray-400 text-xs mt-1">{item.description}</p>
            )}
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <HardDrive className="w-3 h-3" />
                {formatBytes(item.file_size)}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(item.update_date)}
              </span>
              <span className="flex items-center gap-1">
                <Download className="w-3 h-3" />
                {item.download_count}
              </span>
            </div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex gap-1 flex-shrink-0">
            <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-all">
              <Edit className="w-4 h-4" />
            </button>
            <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-500/20 text-gray-400 hover:text-red-400 transition-all">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Password input or direct download */}
      <div className="mt-3 flex gap-2">
        {item.has_password ? (
          <>
            <div className="relative flex-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <input
                type={showPw ? 'text' : 'password'}
                value={passwordInput}
                onChange={(e) => onPasswordChange(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onDownload()}
                placeholder={t('files.enterPassword')}
                className="w-full pl-10 pr-10 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm placeholder-gray-500 focus:border-[#EE1C23]/50 focus:outline-none transition-colors"
              />
              <button
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <button
              onClick={onDownload}
              disabled={downloading || !passwordInput}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EE1C23] to-red-600 text-white text-sm font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {downloading ? (
                <span className="animate-pulse">{t('common.loading')}</span>
              ) : unlocked ? (
                <>
                  <Unlock className="w-4 h-4" />
                  {t('files.downloadAgain')}
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  {t('files.download')}
                </>
              )}
            </button>
          </>
        ) : (
          <button
            onClick={onDownload}
            disabled={downloading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EE1C23] to-red-600 text-white text-sm font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {downloading ? (
              <span className="animate-pulse">{t('common.loading')}</span>
            ) : unlocked ? (
              <>
                <Unlock className="w-4 h-4" />
                {t('files.downloadAgain')}
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                {t('files.download')}
              </>
            )}
          </button>
        )}
      </div>
      {passwordError && (
        <p className="text-red-400 text-xs mt-2">{t('files.wrongPassword')}</p>
      )}
    </div>
  );
}

// ============================================================
// Stats Panel
// ============================================================

function StatsPanel({ files }: { files: ReturnType<typeof useSupabaseFiles> }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState<Awaited<ReturnType<typeof files.getAdminStats>> | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);

  const loadStats = useCallback(() => {
    setStatsError(null);
    files.getAdminStats().then((s) => {
      if (!s) setStatsError(t('files.statsError'));
      else setStats(s);
    });
  }, [files, t]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (statsError) {
    return (
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 mb-8 flex items-center justify-between gap-4 overflow-hidden"
      >
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <span className="text-red-300 text-sm">{statsError}</span>
        </div>
        <button
          onClick={loadStats}
          className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-300 text-sm font-medium transition-all flex-shrink-0"
        >
          <RefreshCw className="w-4 h-4" />
          {t('files.retry')}
        </button>
      </motion.div>
    );
  }

  if (!stats) return null;

  const totalDownloads = stats.items.reduce((sum, i) => sum + i.download_count, 0);

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="bg-black/40 backdrop-blur-md border border-[#EE1C23]/20 rounded-2xl p-6 mb-8 overflow-hidden"
    >
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-[#EE1C23]" />
        <h3 className="text-white font-semibold">{t('files.statistics')}</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white/5 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-[#EE1C23]">{totalDownloads}</div>
          <div className="text-gray-400 text-xs mt-1">{t('files.totalDownloadsAll')}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-white">{stats.items.length}</div>
          <div className="text-gray-400 text-xs mt-1">{t('files.totalFiles')}</div>
        </div>
        <div className="bg-white/5 rounded-lg p-4 text-center">
          <div className="text-3xl font-bold text-white">{stats.recentDownloads.length}</div>
          <div className="text-gray-400 text-xs mt-1">{t('files.recentDownloads')}</div>
        </div>
      </div>
      <div className="space-y-2">
        <h4 className="text-gray-300 text-sm font-medium mb-2">{t('files.perFileStats')}</h4>
        {stats.items.map((item) => (
          <div key={item.id} className="flex items-center justify-between bg-white/5 rounded-lg px-4 py-2">
            <span className="text-gray-300 text-sm truncate">{item.name}</span>
            <span className="text-[#EE1C23] font-semibold text-sm flex items-center gap-1">
              <Download className="w-3 h-3" />
              {item.download_count}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

// ============================================================
// Category Modal
// ============================================================

function CategoryModal({ category, onClose, onSave }: {
  category: FileCategory | null;
  onClose: () => void;
  onSave: (name: string, desc: string, order: number) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(category?.name ?? '');
  const [desc, setDesc] = useState(category?.description ?? '');
  const [order, setOrder] = useState(category?.order_index ?? 0);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setModalError(null);
    try {
      await onSave(name, desc, order);
    } catch (err: any) {
      setModalError(err.message ?? t('common.error'));
    }
    setSaving(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 border border-[#EE1C23]/30 rounded-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {category ? t('files.editCategory') : t('files.addCategory')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-sm mb-1 block">{t('files.categoryName')}</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm focus:border-[#EE1C23]/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm mb-1 block">{t('files.categoryDescription')}</label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm focus:border-[#EE1C23]/50 focus:outline-none resize-none"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm mb-1 block">{t('files.order')}</label>
            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm focus:border-[#EE1C23]/50 focus:outline-none"
            />
          </div>
        </div>
        {modalError && (
          <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span className="text-red-300 text-xs">{modalError}</span>
            </div>
            <button
              onClick={() => { setModalError(null); handleSave(); }}
              className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-medium transition-all flex-shrink-0"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {t('files.retry')}
            </button>
          </div>
        )}
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg bg-white/10 text-gray-300 text-sm font-medium hover:bg-white/20 transition-all">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim()}
            className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EE1C23] to-red-600 text-white text-sm font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all disabled:opacity-40"
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ============================================================
// Item Modal (upload/edit file)
// ============================================================

function ItemModal({ item, categories, onClose, onSave }: {
  item: FileItem | null;
  categories: FileCategory[];
  onClose: () => void;
  onSave: (data: { name: string; description: string; categoryId: string; password: string; updateDate: string; order: number; file: File | null }, onProgress?: (percent: number) => void) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(item?.name ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [categoryId, setCategoryId] = useState(item?.category_id ?? categories[0]?.id ?? '');
  const [password, setPassword] = useState('');
  const [updateDate, setUpdateDate] = useState(item?.update_date ?? new Date().toISOString().split('T')[0]);
  const [order, setOrder] = useState(item?.order_index ?? 0);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [modalError, setModalError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!name.trim() || !categoryId) return;
    if (!item && !file) { setModalError(t('files.selectFile')); return; }
    if (file && file.size > 100 * 1024 * 1024) { setModalError(t('files.fileTooLarge')); return; }

    setSaving(true);
    setUploadProgress(0);
    setModalError(null);
    try {
      await onSave({ name, description, categoryId, password, updateDate, order, file }, (pct) => setUploadProgress(pct));
    } catch (err: any) {
      setModalError(err.message ?? t('common.error'));
    }
    setSaving(false);
    setUploadProgress(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-gray-900 border border-[#EE1C23]/30 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">
            {item ? t('files.editFile') : t('files.addFile')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-gray-400 text-sm mb-1 block">{t('files.fileName')}</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm focus:border-[#EE1C23]/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm mb-1 block">{t('files.category')}</label>
            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm focus:border-[#EE1C23]/50 focus:outline-none"
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id} className="bg-gray-900">{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-gray-400 text-sm mb-1 block">{t('files.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm focus:border-[#EE1C23]/50 focus:outline-none resize-none"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm mb-1 block">
              {item ? t('files.newPassword') : t('files.password')} ({t('files.optional')})
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={item ? t('files.keepCurrentPassword') : ''}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm focus:border-[#EE1C23]/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm mb-1 block">{t('files.updateDate')}</label>
            <input
              type="date"
              value={updateDate}
              onChange={(e) => setUpdateDate(e.target.value)}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm focus:border-[#EE1C23]/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm mb-1 block">{t('files.order')}</label>
            <input
              type="number"
              value={order}
              onChange={(e) => setOrder(parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm focus:border-[#EE1C23]/50 focus:outline-none"
            />
          </div>
          <div>
            <label className="text-gray-400 text-sm mb-1 block">
              {t('files.file')} {item && `(${t('files.replaceFile')})`}
            </label>
            <div className="relative">
              <input
                type="file"
                accept="*/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="w-full px-3 py-2 bg-black/40 border border-white/10 rounded-lg text-white text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-[#EE1C23] file:text-white file:text-xs file:font-medium file:cursor-pointer focus:border-[#EE1C23]/50 focus:outline-none"
              />
            </div>
            {file && (
              <p className="text-green-400 text-xs mt-1">
                {file.name} ({formatBytes(file.size)})
              </p>
            )}
            <p className="text-gray-500 text-xs mt-1">{t('files.maxFileSize')}</p>
          </div>
        </div>
        {modalError && (
          <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <span className="text-red-300 text-xs">{modalError}</span>
            </div>
            <button
              onClick={() => { setModalError(null); handleSave(); }}
              className="flex items-center gap-1.5 px-3 py-1 rounded-md bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-medium transition-all flex-shrink-0"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {t('files.retry')}
            </button>
          </div>
        )}
        {saving && uploadProgress > 0 && (
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-gray-300 text-xs font-medium">{t('files.uploading')}</span>
              <span className="text-[#EE1C23] text-xs font-bold">{uploadProgress}%</span>
            </div>
            <div className="w-full h-2.5 bg-black/50 rounded-full overflow-hidden border border-white/10">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${uploadProgress}%` }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="h-full bg-gradient-to-r from-[#EE1C23] to-red-500 rounded-full"
              />
            </div>
          </div>
        )}
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg bg-white/10 text-gray-300 text-sm font-medium hover:bg-white/20 transition-all">
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim() || !categoryId}
            className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-[#EE1C23] to-red-600 text-white text-sm font-semibold hover:from-red-600 hover:to-[#EE1C23] transition-all disabled:opacity-40"
          >
            {saving ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}